import type {
  CollectionReference,
  DocumentData,
  Firestore,
  PartialWithFieldValue,
  WithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  runTransaction,
  writeBatch,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  deleteDocWithError,
  getDocsWithError,
  setDocWithError,
} from '../../firestore-mobx/fetch'
import { modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { RoomStateCollaborationData } from '../../models/RoomStateCollaborationData'
import type { FirestoreRoomStateCollaborationData } from './schema'
import { schema, writeSchema } from './schema'
import type { ZodError } from 'zod'

export * from './schema'

class CollaborationDataParseError extends Error {}

const converter: FirestoreDataConverter<FirestoreRoomStateCollaborationData> = {
  toFirestore: (
    data: PartialWithFieldValue<FirestoreRoomStateCollaborationData>
  ) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      const error = e as ZodError
      throw new CollaborationDataParseError(
        `Failed to parse collaboration data ${snapshot.ref.path}: ${error.message}`
      )
    }
  },
}

const getColRef = (
  firestore: Firestore,
  params: { roomId: string }
): CollectionReference<FirestoreRoomStateCollaborationData, DocumentData> => {
  return collection(
    firestore,
    'room_state',
    params.roomId,
    'collaboration_data'
  ).withConverter(converter)
}

export const getRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: { roomId: string }
) => {
  const ref = getColRef(repository.firestore, params)

  return modelListStream(repository, ref, RoomStateCollaborationData)
}

export const upsertRoomStateCollaborationDataInTransaction = (
  repository: FirebaseRepository,
  params: {
    roomId: string
    items: {
      id: string
      merge?: boolean
      data: Partial<FirestoreRoomStateCollaborationData>
    }[]
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  if (params.items.length === 0) return Promise.resolve()

  return runTransaction(repository.firestore, async (transaction) => {
    for (const item of params.items) {
      const docRef = doc(ref, item.id)
      transaction.set(
        docRef,
        { ...item.data, updatedAt: new Date() },
        { merge: item.merge ?? true }
      )
    }
  })
}

export const upsertRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: {
    id: string
    roomId: string
    data: WithFieldValue<FirestoreRoomStateCollaborationData>
    merge?: boolean
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  const docRef = doc(ref, params.id)

  return setDocWithError(
    docRef,
    { ...params.data, updatedAt: new Date() },
    {
      errorName: 'UpsertCollaborationDataError',
      merge: params.merge ?? true,
    }
  )
}

export const upsertMultipleRoomStateCollaborationDatas = (
  repository: FirebaseRepository,
  params: {
    roomId: string
    datas: {
      id: string
      data: WithFieldValue<FirestoreRoomStateCollaborationData>
    }[]
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  return runTransaction(repository.firestore, async (transaction) => {
    for (const item of params.datas) {
      const docRef = doc(ref, item.id)
      transaction.set(
        docRef,
        { ...item.data, updatedAt: new Date() },
        { merge: true }
      )
    }
  })
}

export const updateRoomStateCollaborationDataOrder = (
  repository: FirebaseRepository,
  params: {
    roomId: string
    questionId?: string
    list: {
      id: string
      order: number
      container: string
      x?: number
      y?: number
    }[]
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  const batch = writeBatch(repository.firestore)

  params.list.forEach((obj) => {
    const docRef = doc(ref, obj.id)
    const payload: Record<string, number | string> = {
      'location.order': obj.order,
      'location.container': obj.container,
    }
    if (obj.x !== undefined) payload['location.x'] = obj.x
    if (obj.y !== undefined) payload['location.y'] = obj.y
    if (params.questionId) {
      payload['questionId'] = params.questionId
    }
    batch.update(docRef, payload)
  })

  return batch.commit().catch((error) => {
    throw new Error(`UpsertCollaborationDataOrderError: ${error.message}`)
  })
}

export const deleteRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: {
    id: string
    roomId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params)

  const docRef = doc(ref, params.id)

  return deleteDocWithError(docRef, 'DeleteCollaborationDataError')
}

export const deleteAllRoomStateCollaborationData = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params)

  const batch = writeBatch(repository.firestore)

  const docs = await getDocsWithError(ref)

  if (docs.empty) return Promise.resolve()

  for (const doc of docs.docs) {
    batch.delete(doc.ref)
  }

  return batch.commit()
}
